/**
 * ApiKeyPanel
 *
 * Capability-keyed setup panel rendered when a connector declares the
 * `api-key` capability. Generalization of the form Tango
 * already uses: a list of credential fields rendered as text inputs. The
 * field schema is supplied by the caller so this panel works for any
 * api-key connector (Tango, Woocommerce, Amazon, ...).
 *
 * Renders the fields flush inside the modal `Stack` — no card chrome and
 * no in-panel action button. The modal footer drives submit.
 *
 * Purely presentational. The caller owns state and dispatching; this
 * keeps the panel reusable across setup surfaces (channels, erps, ...).
 */

import { PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { integrationsTestIds } from '@domains/integrations/testids';

export type ApiKeyField = {
  /** Field key — used as the form data key. */
  name: string;
  /** Translated label. */
  label: string;
  /** Optional placeholder. */
  placeholder?: string;
  /** Render as a password / secret input. */
  secret?: boolean;
  /** Render as a URL field: `https://` left-section + paste strip. */
  isUrl?: boolean;
  /** Validation error message (caller-supplied). */
  error?: string;
};

export type ApiKeyPanelProps = {
  /** Credential field schema. Order is preserved. */
  fields: ApiKeyField[];
  /** Controlled form values keyed by `field.name`. */
  values: Record<string, string>;
  /** Field change handler. */
  onChange: ( name: string, value: string ) => void;
};

const ApiKeyPanel = ({ fields, values, onChange }: ApiKeyPanelProps ) => {
  return (
    <Stack gap="xs">
      {fields.map(( field ) => {
        const value = values[field.name] ?? '';
        const testId =
          field.name === 'apiKey' || field.secret
            ? integrationsTestIds.setupApikeyKeyInput
            : integrationsTestIds.setupApikeyFieldInput( field.name );

        if ( field.isUrl ) {
          return (
            <TextInput
              key={field.name}
              size="sm"
              radius="xs"
              label={field.label}
              placeholder={field.placeholder}
              value={value}
              error={field.error}
              leftSection={<Text size="13px">https://</Text>}
              leftSectionWidth={70}
              onChange={( e ) => onChange( field.name, e.currentTarget.value )}
              onPaste={( e ) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData( 'text' );
                onChange( field.name, pasted.trim().replace( /^https?:\/\//, '' ));
              }}
              data-testid={testId}
            />
          );
        }

        const commonProps = {
          'key': field.name,
          'size': 'sm' as const,
          'radius': 'xs' as const,
          'label': field.label,
          'placeholder': field.placeholder,
          value,
          'error': field.error,
          'onChange': ( e: React.ChangeEvent<HTMLInputElement> ) =>
            onChange( field.name, e.currentTarget.value ),
          'data-testid': testId
        };

        return field.secret ? (
          <PasswordInput {...commonProps} />
        ) : (
          <TextInput {...commonProps} />
        );
      })}
    </Stack>
  );
};

export default ApiKeyPanel;
